import { useEffect, useState } from "react";
import { Loader2, Search, UserCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/** Partner orgs whose name matches nothing in the Stax Bill export.
 *  The automatic backfill gets the obvious ones; a partner trading under a
 *  different name (Hicom, for one) needs a human to say "that's them" — until
 *  someone does, their partner admins see an empty Commissions page. */
export function CommissionAliasFixDialog({ open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState("");
  const [choice, setChoice] = useState({});
  const [q, setQ] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/commissions/aliases/unmatched");
      setData(d);
      // Deliberately NOT pre-selected: the fuzzy suggestion is often wrong and
      // a careless click would write the wrong agent onto a partner.
      setChoice({});
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load the unmatched list");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (org) => {
    const alias = choice[org.org_id];
    if (!alias) return;
    setSaving(org.org_id);
    try {
      const { data: r } = await api.put(`/commissions/aliases/${org.org_id}`, { alias });
      toast.success(`${org.name} → ${r.alias} · ${r.rows_visible} row${r.rows_visible === 1 ? "" : "s"} now visible to them`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't set that alias");
    } finally { setSaving(""); }
  };

  const agents = data?.agents || [];
  const orgs = data?.orgs || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="alias-fix-dialog">
        <DialogHeader>
          <DialogTitle>Partners with no commission match</DialogTitle>
          <DialogDescription>
            These partner organisations don’t match any agent name in the Stax Bill export, so
            their partner admins can’t see any commission rows. Pick the agent that is them.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex h-32 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <p className="text-xs text-muted-foreground" data-testid="alias-fix-summary">
              {orgs.length} partner org{orgs.length === 1 ? "" : "s"} unmatched ·{" "}
              {agents.length} agent name{agents.length === 1 ? "" : "s"} in the export with no partner ·{" "}
              {data?.claimed || 0} already matched
            </p>

            <div className="max-h-[420px] space-y-2 overflow-y-auto">
              {orgs.map((o) => {
                const needle = (q[o.org_id] || "").toLowerCase();
                const list = needle
                  ? agents.filter((a) => a.agent.toLowerCase().includes(needle)).slice(0, 40)
                  : [...(o.suggestions || []).map((s) => ({ agent: s, rows: agents.find((a) => a.agent === s)?.rows || 0 })),
                    ...agents.filter((a) => !(o.suggestions || []).includes(a.agent)).slice(0, 20)];
                return (
                  <div key={o.org_id} data-testid={`alias-fix-row-${o.org_id}`}
                    className="rounded-lg border border-border p-2.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-foreground">{o.name}</span>
                      <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase text-muted-foreground">
                        {o.partner_type || "partner"}
                      </span>
                      {o.commission_alias && (
                        <span className="text-[11px] text-amber-600 dark:text-amber-400">
                          current alias “{o.commission_alias}” matches nothing
                        </span>
                      )}
                      <Button size="sm" className="ml-auto h-7 text-xs" onClick={() => save(o)}
                        disabled={!choice[o.org_id] || saving === o.org_id}
                        data-testid={`alias-fix-save-${o.org_id}`}>
                        {saving === o.org_id
                          ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                          : <UserCheck className="mr-1.5 h-3.5 w-3.5" />}
                        That’s them
                      </Button>
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <div className="relative w-52">
                        <Search className="absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
                        <Input value={q[o.org_id] || ""} placeholder="Search agent names"
                          data-testid={`alias-fix-search-${o.org_id}`}
                          onChange={(e) => setQ({ ...q, [o.org_id]: e.target.value })}
                          className="h-7 pl-7 text-xs" />
                      </div>
                      <select value={choice[o.org_id] || ""} data-testid={`alias-fix-select-${o.org_id}`}
                        onChange={(e) => setChoice({ ...choice, [o.org_id]: e.target.value })}
                        className="h-7 flex-1 rounded-md border border-border bg-background px-2 text-xs">
                        <option value="">Choose the agent in the export…</option>
                        {list.map((a) => (
                          <option key={a.agent} value={a.agent}>
                            {a.agent}{a.rows ? ` · ${a.rows} rows` : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                );
              })}
              {orgs.length === 0 && (
                <p className="py-6 text-center text-sm text-muted-foreground" data-testid="alias-fix-empty">
                  Every partner org matches an agent in the export — nothing to fix.
                </p>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
